


















import { useAlgoliaConfigStore } from '@algolia-search/stores/config';
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  ssrRef,
  useContext,
  useFetch,
  useMeta
} from '@nuxtjs/composition-api';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import { onSSR } from '@vue-storefront/core';
import type { TranslateResult } from 'vue-i18n/types';

import {
  useCanonical,
  usePreviewMode,
  useRequestPath,
  useStructuredData
} from '~/diptyqueTheme/composable';
import { useContentfulPreview } from '~/diptyqueTheme/composable/useContentfulPreview';
import { useHreflangs } from '~/diptyqueTheme/composable/useHreflangs';
import Page404 from '~/diptyqueTheme/pages/Page404.vue';
import { Logger } from '~/helpers/logger';
import { useCategoryStore } from '~/modules/catalog/category/stores/category';
import type {
  CategoryListingLayout,
  CategoryTree
} from '~/modules/GraphQL/types';
import { CategoryListingLayoutEnum } from '~/modules/GraphQL/types';

export default defineComponent({
  name: 'CategoryPage',
  components: {
    Page404,
    VaimoBreadcrumbs: () => import('atoms/VaimoBreadcrumbs.vue'),
    CategoryDefault: () => import('templates/category/CategoryDefault.vue'),
    CategoryAlgolia: () => import('templates/category/CategoryAlgolia.vue'),
    CategoryCollection: () =>
      import('templates/category/CategoryCollection.vue'),
    CategoryMain: () => import('templates/category/CategoryMain.vue'),
    CategoryHeading: () =>
      import('templates/category/parts/CategoryHeading.vue'),
    CategoryDescription: () =>
      import('templates/category/parts/CategoryDescription.vue')
  },
  setup() {
    const {
      getFullCategoryBasedOnRequestPath,
      getBreadcrumbsList,
      getBreadcrumbs
    } = useRequestPath();
    const {
      app: { i18n },
      error: nuxtError
    } = useContext();
    const { addTags } = useCache();
    const activeCategory = ref<CategoryTree | null>(null);
    const loading = ref<boolean | null>(true);
    const noCategoryData = ref<boolean | null>(false);
    const { getCategoryBreadcrumbsStructuredData } = useStructuredData();
    const { getCategoryLink, categoryURL } = useContentfulPreview();
    const { isPreviewMode } = usePreviewMode();
    const { setCategoryType, resetCategoryType, setCategoryId } =
      useCategoryStore();
    const { replaceCategories } = useAlgoliaConfigStore();

    const breadcrumbs = ssrRef([]);

    onSSR(async () => {
      const breadcrumbsList = await getBreadcrumbsList('category');
      breadcrumbs.value = getBreadcrumbs(breadcrumbsList, true);
    });

    const fetchCategoryData = async () => {
      if (isPreviewMode.value) {
        await getCategoryLink();
      }

      try {
        const result = await getFullCategoryBasedOnRequestPath(
          categoryURL.value
        );
        if (result) {
          activeCategory.value = result;
          setCategoryType(result.listing_layout as CategoryListingLayoutEnum);
          setCategoryId(result.id);
        } else {
          noCategoryData.value = true;
          setInitialLoadFromServer(false);
          setCategoryId(null);
          return nuxtError({ statusCode: 404 });
        }
      } catch (error) {
        noCategoryData.value = true;
        setCategoryId(null);
        Logger.error(
          'Get Full Product Category from URL path by route [ERROR]',
          error
        );
        Logger.error(error);
        return nuxtError({ statusCode: 404 });
      }
    };

    const initialLoadFromServer = ref(true);
    const { canonicalCategoryLink } = useCanonical();

    const setInitialLoadFromServer = (state: boolean) => {
      initialLoadFromServer.value = state;
    };

    useFetch(async () => {
      loading.value = true;
      setInitialLoadFromServer(process.server);
      await fetchCategoryData();
      loading.value = false;

      const tags = [{ prefix: CacheTagPrefix.View, value: 'category' }];
      addTags(tags);
    });

    /**
     * Determine listing layout based on category data
     * - If Algolia replaceCategories is enabled, override layout to PLP_ALGOLIA
     * - Otherwise, use the 'listing_layout' from category data
     */
    const listingLayout = computed<CategoryListingLayout>(() => {
      const layout =
        activeCategory.value?.listing_layout || CategoryListingLayoutEnum.PLP;

      // Override layout to PLP_ALGOLIA if configured in Algolia settings
      if (layout === CategoryListingLayoutEnum.PLP && replaceCategories.value) {
        return CategoryListingLayoutEnum.PLP_ALGOLIA;
      }

      return layout;
    });

    /**
     * Map listing layout to component name
     * - COLLECTION is custom layout for collection category pages
     * - MAIN is custom layout for main category pages
     * - PLP is default layout for standard category pages
     * - PLP_ALGOLIA is custom layout for Algolia powered category pages
     */
    const componentsMap: Record<CategoryListingLayout, string> = {
      [CategoryListingLayoutEnum.COLLECTION]: 'CategoryCollection',
      [CategoryListingLayoutEnum.MAIN]: 'CategoryMain',
      [CategoryListingLayoutEnum.PLP]: 'CategoryDefault',
      [CategoryListingLayoutEnum.PLP_ALGOLIA]: 'CategoryAlgolia'
    };

    /**
     * Determine category component name based on listing layout
     */
    const categoryComponentName = computed(
      () => componentsMap[listingLayout.value]
    );

    /**
     * Meta title and description generation
     */
    const metaPrefixes: Record<string, string | TranslateResult> = {
      [CategoryListingLayoutEnum.MAIN]: i18n.t('Discover the universe'),
      [CategoryListingLayoutEnum.COLLECTION]: i18n.t('Discover the collection'),
      [CategoryListingLayoutEnum.PLP]: i18n.t('Discover the products')
    };

    /**
     * Generate meta title
     * - If meta_title is set in category, use it
     * - Otherwise, use "Category Name | Diptyque Paris"
     */
    const metaTitle = computed<string>(() => {
      const categoryName = activeCategory.value?.name ?? '';

      return (
        activeCategory.value?.meta_title || `${categoryName} | Diptyque Paris`
      );
    });

    /**
     * Generate meta description
     * - If meta_description is set in category, use it
     * - Otherwise, construct description using prefix, category name, and suffix
     */
    const metaDescription = computed<string>(() => {
      const prefix =
        metaPrefixes[listingLayout.value] || i18n.t('Discover the products');
      const categoryName = activeCategory.value?.name ?? '';
      const suffix = i18n.t(
        'Diptyque, a precursor of an art of living through the senses, where perfume and art permeate everything.'
      );

      const description = [prefix, categoryName, suffix]
        .filter(Boolean)
        .join(' ');

      return activeCategory.value.meta_description || description || '';
    });

    const metaTags = computed(() =>
      activeCategory.value
        ? {
            title: metaTitle.value,
            meta: [
              {
                hid: 'og:title',
                property: 'og:title',
                content: metaTitle.value
              },
              {
                hid: 'description',
                name: 'description',
                content: metaDescription.value
              },
              {
                hid: 'og:description',
                name: 'og:description',
                content: metaDescription.value
              }
            ],
            link: [canonicalCategoryLink(activeCategory.value)],
            script: [
              {
                type: 'application/ld+json',
                json: getCategoryBreadcrumbsStructuredData(breadcrumbs.value)
              }
            ]
          }
        : {}
    );

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    useMeta(() => metaTags.value);

    onMounted(() => {
      fetchCategoryData();
    });

    onUnmounted(() => {
      resetCategoryType();
      setCategoryId(null);
    });

    return {
      categoryComponentName,
      loading,
      noCategoryData,
      activeCategory,
      listingLayout,
      breadcrumbs
    };
  },

  async asyncData(ctx) {
    const { getHrefLangs } = useHreflangs(ctx);
    const hrefLangsLinks = await getHrefLangs();
    return {
      hrefLangsLinks
    };
  },
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  head() {
    return {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      link: this.hrefLangsLinks
    };
  }
});
